/* global window */
import { CartContext } from './context/CartContext';
import ColorSwatch from './ColorSwatch';
import { Form, Row } from 'react-bootstrap';
import InputQuantity from './InputQuantity';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import '../styles/ProductAddToCartForm.css';
import ProductOption from './ProductOption';
import AppContext from './context/AppContext';
import { ToastContext } from './context/ToastContext';

/**
 * Component for showing the "add to cart" form with or without options.
 * @component
 */
const ProductAddToCartForm = ({
    isAllOptionsSelected,
    isProductAvailableForSale,
    maxQuantity,
    options,
    price,
    selectedOptions,
    selectOption,
    selectedVariant,
    variants,
    totalCartQuantity,
}) => {
    /** Contexts  */
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const {
        addLineItem,
        cartState,
        cartUpdating,
        checkoutData,
    } = useContext(CartContext);
    const { sendMessage } = useContext(ToastContext);

    /** States */
    const [ quantity, setQuantity ] = useState(1);

    const defaultVariant = useMemo(
        () => variants.find(
            (variant) => (variant.title === 'Default Title'
                || variant.variantPosition === 1)
        ) ?? null,
        [ variants ],
    );

    const getSelectedOption = useCallback(
        (id) => selectedOptions.get(id) ?? '',
        [ selectedOptions ],
    );

    const hasOptions = useMemo(
        () => options.some(
            (option) => option.optionValue.split(', ').length > 1
        ),
        [ options ],
    );

    const isVariantAvailable = useMemo(
        () => hasOptions
            ? (selectedVariant
                ? selectedVariant.availableForSale === true
                : defaultVariant?.availableForSale === true ?? false)
            : isProductAvailableForSale === true,
        [ isProductAvailableForSale, hasOptions, selectedVariant ],
    );

    // Create text for selected variant options.
    const selectedVariantText = useMemo(
        () => options.sort((a, b) => a.optionPosition - b.optionPosition)
            .map(option => getSelectedOption(option.id))
            .filter(value => value !== '').join(' / '),
        [ options ],
    );

    // Calculate price difference.
    const priceDiff = useMemo(
        () => !selectedVariant?.price || price === selectedVariant?.price
            ? 0
            : Math.round((selectedVariant.price - price) * 100) / 100,
        [ price, selectedVariant ],
    );
    const priceDiffText = useMemo(
        () => priceDiff !== 0
            ? ` [${priceDiff > 0 ? '+' : '-'}$${Math.abs(priceDiff)} ${appConfig.storeCurrency}]`
            : '',
        [appConfig.storeCurrency, priceDiff],
    );

    const selectedVariantStatus = useMemo(
        () => selectedVariant
            ? (isVariantAvailable ? '' : 'Out of stock')
            : ((!hasOptions || isAllOptionsSelected)
                ? 'Not available'
                : 'Please select more options...'),
        [ selectedVariant ],
    );

    /** Form Event Handlers */
    const handleFormSubmission = useCallback(async (e, checkout = false) => {
        e.preventDefault();
        e.stopPropagation();
        let result;
        if (checkout === false
            || (totalCartQuantity < 1
                && (selectedVariant
                    || defaultVariant.availableForSale === true
                )
            )
        ) {
            const availableInventoryLevels = selectedVariant
                ? selectedVariant.availableInventoryLevels ?? []
                : defaultVariant?.availableInventoryLevels ?? [];
            let lineItems = [];
            let quantityToAdd = quantity;
            for (const inventoryLevel of availableInventoryLevels) {
                const cartItem = cartState.find(
                    (lineItem) => lineItem.inventoryLevelId === inventoryLevel.id
                );
                const currentQuantity = Math.min(
                    quantityToAdd,
                    (inventoryLevel.bypassNegativeQuantity === true
                        ? Infinity
                        : inventoryLevel.availableQuantity
                    ) - (cartItem?.quantity ?? 0),
                );
                lineItems.push({
                    inventoryLevelId: inventoryLevel.id,
                    quantity: currentQuantity,
                });
                quantityToAdd -= currentQuantity;
                if (quantityToAdd <= 0) {
                    break;
                }
            }
            lineItems = lineItems.filter((lineItem) => lineItem.quantity > 0);
            result = lineItems.length > 1
                ? await addLineItem(null, null, lineItems, () => setQuantity(1))
                : await addLineItem(
                    lineItems[0].inventoryLevelId,
                    quantity,
                    null,
                    () => setQuantity(1),
                );
        }
        if (result?.success === false) {
            return sendMessage(
                result.error ?? 'There was a problem adding the item to the '
                    + 'cart. Please try again later.',
                'danger',
            );
        }
        if (checkout === true) {
            if ((!result || result?.success === true) && checkoutData.webUrl) {
                return window.location.assign(checkoutData.webUrl);
            }
            sendMessage(
                'We couldn\'t redirect you to the checkout page right now. '
                    + 'Please try using the cart icon in the top right corner '
                    + 'of the page.',
                'info',
            );
        }
    }, [
        addLineItem,
        cartState,
        checkoutData,
        defaultVariant,
        quantity,
        totalCartQuantity,
        selectedVariant,
        sendMessage,
        setQuantity,
    ]);

    /** Get global cart quantity (of all items) */
    const globalCartQty = useMemo(() => cartState
        ?.map((item) => item.quantity)
        .reduce((prev, cur) => +prev + +cur, 0)
    ?? 0, [cartState]);

    /** Get variants that overlap with the selected options */
    const variantsWithSelectedOptions = useCallback((excludeLastOption = false) => {
        const resultVariants = [];
        const selectedOptsArr = [...selectedOptions.entries()];
        if (excludeLastOption === true) {
            selectedOptsArr.splice(-1, 1);
        }
        variants_loop:
        for (const variant of variants) {
            for (const selectedOpt of selectedOptsArr) {
                if (variant.Options.find(
                    (vOpt) => vOpt.id === selectedOpt[0])
                    ?.VariantSelectedOptions.optionValueSelected !== selectedOpt[1]
                )
                    continue variants_loop;
            }
            resultVariants.push(variant);
        }
        return resultVariants;
    }, [variants, selectedOptions]);

    /* Check if the option Id provided is the last on the selected options */
    const isLastOptionFromSelected = useCallback((optionId) => {
        const selectedOptsArr = [...selectedOptions.keys()];
        return (selectedOptsArr.at(-1) === optionId);
    }, [selectedOptions]);

    /* Returns true if there are no selections */
    const allOptionsEmpty = useMemo(
        () => options.every((o) => getSelectedOption(o.id) === ''),
        [options, getSelectedOption]
    );

    /* Render the layout */
    return <Form
        className={'addtocart--form'}
        onSubmit={handleFormSubmission}>
        {/**
         * @todo Move "Selected Variant" and "SKU" info to a position where it
         * doesn't obstruct or change the posiion of other input/button elements.
        */}
        {selectedOptions.size > 0
            && <div
                className={'addtocart--variant-info'}>
                <span>
                    {isVariantAvailable ? 'Variant' : 'You selected'}:&nbsp;
                    {selectedVariant ? selectedVariant.title : selectedVariantText}
                    {priceDiffText}
                </span>
                {(isVariantAvailable
                    && selectedVariant?.InventoryItem.sku)
                    && <span
                        className={'addtocart--product-sku'}>
                        <strong>SKU</strong>: {selectedVariant.InventoryItem.sku}
                    </span>
                }
                {selectedVariantStatus !== ''
                    && <span
                        className={`addtocart--product-status${selectedVariantStatus === 'Not available' ? ' unavailable' : ''}`}>
                        {selectedVariantStatus}
                    </span>}
            </div>
        }
        {!(options.length === 1
            && options[0].optionName === 'Title'
            && options[0].optionValue === 'Default Title'
        ) && options
            .sort((a, b) => a.optionPosition - b.optionPosition)
            .map((option, index) => option.optionName.toLowerCase() === 'color'
                ? <div
                    className={'addtocart--option'}
                    key={`option-${index}`}>
                    <label>Color</label>
                    <div
                        className={'addtocart--horizontal-option-list'}>
                        {option.optionValue
                            .split(', ')
                            .map((value, index) => <ColorSwatch
                                key={`${option.optionName}-${index}`}
                                colorName={value}
                                size={'3rem'}
                                isActive={getSelectedOption(option.id) === value}
                                onClick={() => selectOption(option.id, value)}
                            />)
                        }
                    </div>
                </div>
                : <div
                    key={`option-${index}`}
                    className={'addtocart--option'}>
                    <ProductOption
                        option={option}
                        allOptionsEmpty={allOptionsEmpty}
                        variantsWithSelectedOptions={variantsWithSelectedOptions}
                        isLastOptionFromSelected={isLastOptionFromSelected}
                        selectOption={selectOption}
                        defaultValue={getSelectedOption(option.id)} />
                </div>
            )
        }
        {(!hasOptions || isAllOptionsSelected) && <div
            className={'addtocart--option'}>
            <label>Quantity</label>
        </div>}
        <div
            className={'addtocart--submit-section'}>
            <InputQuantity
                max={maxQuantity}
                quantity={quantity}
                setQuantity={setQuantity}
            />
            <Row
                className={'addtocart--row'}>
                <button
                    className={'addtocart--submit-button'}
                    type={'submit'}
                    disabled={(cartUpdating || !isVariantAvailable || maxQuantity < 1 || (hasOptions && !isAllOptionsSelected))}>
                    {selectedVariant
                        ? (maxQuantity > 0 ? 'Add to cart': 'Out of stock')
                        : 'Add to cart'
                    }
                </button>
                <button
                    className={'addtocart--submit-button'}
                    disabled={(cartUpdating || !isVariantAvailable)}
                    type={'submit'}
                    onClick={(e) => (globalCartQty || (isVariantAvailable && (!hasOptions || isAllOptionsSelected))) ? handleFormSubmission(e, true) : undefined}
                >Buy Now</button>
            </Row>
            <p
                className={'mt-2 mb-2 addtocart--product-status available'}>
                {hasOptions
                    && !isAllOptionsSelected
                    && !selectedVariant
                    && defaultVariant
                    && <>Default variant: {defaultVariant.title ?? 'None'}<br /></>}
                {checkoutData && totalCartQuantity > 0
                    ? `You added ${totalCartQuantity} item(s) of this ${hasOptions ? 'variant' : 'product'} to your cart.`
                    : (!selectedVariant ? 'This variant will be added to your cart when you click "Buy Now".' : '')
                }
            </p>
        </div>
    </Form>;
};

ProductAddToCartForm.propTypes = {
    isAllOptionsSelected: bool,
    isProductAvailableForSale: bool,
    maxQuantity: number,
    options: array,
    price: oneOfType([number, string]),
    selectedOptions: object,
    selectOption: func,
    selectedVariant: object,
    variants: array,
    totalCartQuantity: number,
};

export default ProductAddToCartForm;
